/* eslint-disable no-mixed-operators */
import { useState } from 'react';
import {
    ChevronLeft, Search, Plus, Check, MapPin,
    Upload, ChevronDown
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Navigate } from 'react-router-dom';

export default function CreateNewInvoice() {
    const [step, setStep] = useState(1);
    const [selectedClient, setSelectedClient] = useState(null);
    const [showClientDropdown, setShowClientDropdown] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedJobs, setSelectedJobs] = useState([]);
    const [invoiceSubject, setInvoiceSubject] = useState('Subject');
    const [issuedDate,] = useState('Nov 16, 2025');
    const [paymentDue,] = useState('Net 30');
    const [salesperson] = useState({ name: 'Zaid', avatar: 'Z' });

    const clients = [
        { id: 1, name: "as", title: "Mr. as as", phone: "0792019411", email: "dd@gmail.com", properties: 1, lastActive: "16 minutes ago" },
    ];

    const jobs = [
        { id: 1, number: "#1", title: "as", address: "sd\\ds\\sd, sd 12235", type: "One-off", uninvoiced: "$0.00", hasItems: false },
        { id: 2, number: "#2", title: "One-off job", address: "sd\\ds\\sd, sd 12235", type: "One-off", uninvoiced: "$0.00", hasItems: false },
    ];

    const filteredClients = clients.filter(c =>
        c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.phone.includes(searchTerm) ||
        c.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const toggleJob = (id) => {
        setSelectedJobs(prev =>
            prev.includes(id) ? prev.filter(j => j !== id) : [...prev, id]
        );
    };

    const nextStep = () => setStep(prev => Math.min(prev + 1, 3));
    const prevStep = () => setStep(prev => Math.max(prev - 1, 1));

    return (
        <section className="min-h-screen  text-sm">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 rounded-xl shadow-sm">
                <div className="px-4 py-4">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                            <Link to="/profile/invoices" className="p-2 hover:bg-gray-100 rounded-lg transition">
                                <ChevronLeft className="w-5 h-5" />
                            </Link>
                            <div>
                                <h1 className="text-xl font-bold text-gray-900">
                                    {step === 1 ? "New Invoice" : `Invoice for ${selectedClient?.name || 'Client'}`}
                                </h1>
                                <p className="text-xs text-gray-500">
                                    {step === 1 ? "Select client" : step === 2 ? "Select jobs" : "Invoice details"}
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-gray-600">Step {step} of 3</span>
                            <div className="flex gap-1">
                                {[1, 2, 3].map(i => (
                                    <div key={i} className={`w-6 h-1.5 rounded-full transition ${i <= step ? 'bg-teal-600' : 'bg-gray-300'}`} />
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Step 1: Select Client */}
                    {step === 1 && (
                        <div className="space-y-3">
                            <label className="block text-xs font-medium text-gray-700">Which client would you like to create this for?</label>
                            <div className="relative">
                                <button
                                    onClick={() => setShowClientDropdown(!showClientDropdown)}
                                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-left flex items-center justify-between hover:border-teal-400 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 text-sm bg-white"
                                >
                                    {selectedClient ? (
                                        <div className="flex items-center gap-2">
                                            <div className="w-8 h-8 bg-gradient-to-br from-teal-400 to-teal-500 rounded-md flex items-center justify-center text-white font-bold text-xs">
                                                {selectedClient.name[0]}
                                            </div>
                                            <div>
                                                <p className="font-medium text-gray-900 text-xs">{selectedClient.name} ({selectedClient.title})</p>
                                                <p className="text-xs text-gray-500">{selectedClient.properties} Property | {selectedClient.phone}</p>
                                            </div>
                                        </div>
                                    ) : (
                                        <span className="text-gray-500">Search clients...</span>
                                    )}
                                    <Search className="w-4 h-4 text-gray-400" />
                                </button>

                                {showClientDropdown && (
                                    <>
                                        <div className="fixed inset-0 z-40 bg-black/20" onClick={() => setShowClientDropdown(false)} />
                                        <div className="absolute top-full left-0 right-0 mt-1.5 bg-white rounded-lg shadow-xl border border-gray-200 z-50 text-xs">
                                            <div className="p-3 bg-gradient-to-r from-teal-500 to-teal-600">
                                                <div className="relative">
                                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/70" />
                                                    <input
                                                        type="text"
                                                        placeholder="Search..."
                                                        value={searchTerm}
                                                        onChange={(e) => setSearchTerm(e.target.value)}
                                                        className="w-full pl-10 pr-3 py-2 bg-white/20 rounded-md text-white placeholder-white/70 focus:outline-none"
                                                        autoFocus
                                                    />
                                                </div>
                                            </div>
                                            <div className="max-h-60 overflow-y-auto">
                                                {filteredClients.length > 0 ? (
                                                    filteredClients.map((client) => (
                                                        <button
                                                            key={client.id}
                                                            onClick={() => {
                                                                setSelectedClient(client);
                                                                setShowClientDropdown(false);
                                                                setSearchTerm('');
                                                            }}
                                                            className="w-full text-left px-3 py-2.5 hover:bg-teal-50 flex items-center gap-2 border-b border-gray-100 last:border-0"
                                                        >
                                                            <div className="w-9 h-9 bg-gradient-to-br from-teal-400 to-teal-500 rounded-md flex items-center justify-center text-white font-bold text-xs">
                                                                {client.name[0]}
                                                            </div>
                                                            <div className="flex-1">
                                                                <p className="font-medium text-gray-900 text-xs">{client.name} ({client.title})</p>
                                                                <p className="text-xs text-gray-500">{client.properties} Property | {client.phone}</p>
                                                                <p className="text-xs text-gray-400">Active {client.lastActive}</p>
                                                            </div>
                                                        </button>
                                                    ))
                                                ) : (
                                                    <div className="px-3 py-6 text-center text-gray-500 text-xs">No clients</div>
                                                )}
                                            </div>
                                            <div className="p-2 bg-gray-50 border-t">
                                                <button className="w-full py-1.5 text-xs font-medium text-teal-600 hover:text-teal-700 flex items-center justify-center gap-1">
                                                    <Plus className="w-3.5 h-3.5" /> Create New Client
                                                </button>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Step 2: Select Jobs */}
                    {step === 2 && selectedClient && (
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-2">
                                Select the jobs you want to invoice:
                            </label>
                            <div className="space-y-2">
                                {jobs.map(job => (
                                    <div
                                        key={job.id}
                                        onClick={() => toggleJob(job.id)}
                                        className={`border rounded-lg p-3 cursor-pointer transition ${selectedJobs.includes(job.id) ? 'border-teal-500 bg-teal-50' : 'border-gray-300 hover:border-teal-400'}`}
                                    >
                                        <div className="flex items-start justify-between">
                                            <div className="flex items-start gap-2">
                                                <div className="mt-0.5">
                                                    {selectedJobs.includes(job.id) ? <Check className="w-4 h-4 text-teal-600" /> : <div className="w-4 h-4 border border-gray-400 rounded" />}
                                                </div>
                                                <div>
                                                    <p className="font-medium text-gray-900 text-xs">{job.number} {job.title}</p>
                                                    <p className="text-xs text-gray--600 flex items-center gap-1">
                                                        <MapPin className="w-3 h-3" />
                                                        {job.address.replace(/\\n/g, ', ')}
                                                    </p>
                                                    <p className="text-xs text-gray-500">{job.type}</p>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <p className="font-medium text-gray-900">{job.uninvoiced}</p>
                                                <p className="text-xs text-gray-500">{job.hasItems ? 'Has items' : 'No line items'}</p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Step 3: Invoice Details */}
                    {step === 3 && selectedClient && (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <h2 className="text-base font-bold text-gray-900">Invoice for {selectedClient.name}</h2>
                                <button className="text-xs text-teal-600 hover:text-teal-700 font-medium">Invoice #1 Change</button>
                            </div>

                            {/* Invoice Subject */}
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Invoice subject</label>
                                <input
                                    type="text"
                                    value={invoiceSubject}
                                    onChange={(e) => setInvoiceSubject(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500 text-sm"
                                />
                            </div>

                            {/* Addresses */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Billing address</label>
                                    <p className="text-xs text-gray-700">sd ds sd, sd 12235</p>
                                    <p className="text-xs text-gray-500 mt-1">Property Address (Same as billing address)</p>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1">Contact details</label>
                                    <p className="text-xs text-gray-700">{selectedClient.phone}{selectedClient.email}</p>
                                </div>
                            </div>

                            {/* Invoice Details */}
                            <div className="grid grid-cols-2 gap-4 text-xs">
                                <div>
                                    <label className="text-gray-700">Issued date</label>
                                    <p className="font-medium mt-0.5">{issuedDate}</p>
                                </div>
                                <div>
                                    <label className="text-gray-700">Payment due</label>
                                    <p className="font-medium mt-0.5">{paymentDue}</p>
                                </div>
                                <div>
                                    <label className="text-gray-700">Salesperson</label>
                                    <div className="flex items-center gap-1.5 mt-0.5">
                                        <div className="w-7 h-7 rounded-full bg-emerald-600 text-white flex items-center justify-center text-xs font-bold">
                                            {salesperson.avatar}
                                        </div>
                                        <span className="font-medium">{salesperson.name}</span>
                                    </div>
                                </div>
                                <div className="flex items-center justify-between mt-1">
                                    <span className="text-gray-600">Need to track more details?</span>
                                    <button className="text-teal-600 hover:text-teal-700 text-xs font-medium">+ Add Custom Field</button>
                                </div>
                            </div>

                            <div className="flex items-center justify-between py-2 border-b border-gray-200">
                                <span className="text-xs text-gray-600">Invoice follow-ups</span>
                                <button className="text-teal-600 hover:text-teal-700 text-xs font-medium">Change Schedule</button>
                            </div>

                            {/* Line Items */}
                            <div>
                                <div className="grid grid-cols-12 text-xs font-medium text-gray-700 border-b border-gray-200 pb-1">
                                    <div className="col-span-6">Product / Service</div>
                                    <div className="col-span-2 text-center">Qty.</div>
                                    <div className="col-span-2 text-right">Unit Price</div>
                                    <div className="col-span-2 text-right">Total</div>
                                </div>
                                <div className="py-3 text-center text-xs text-gray-500">
                                    No line items
                                </div>
                                <button className="w-full py-2 border border-dashed border-gray-300 rounded-lg text-xs text-teal-600 hover:border-teal-500 hover:bg-teal-50 flex items-center justify-center gap-1">
                                    <Plus className="w-3.5 h-3.5" /> Add Line Item
                                </button>
                            </div>

                            {/* Totals */}
                            <div className="space-y-1 text-xs">
                                <div className="flex justify-between"><span>Subtotal</span><span className="font-medium">$0.00</span></div>
                                <div className="flex justify-between text-teal-600"><span>Discount</span><span className="font-medium">Add Discount</span></div>
                                <div className="flex justify-between text-teal-600"><span>Tax</span><span className="font-medium">Add Tax</span></div>
                                <div className="flex justify-between font-bold text-sm py-1 border-t border-gray-300"><span>Total</span><span>$0.00</span></div>
                                <div className="flex justify-between text-teal-600"><span>Deposits</span><span className="font-medium">Add Deposit</span></div>
                            </div>

                            {/* Messages */}
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Client message</label>
                                <textarea rows="2" className="w-full p-2.5 border border-gray-300 rounded-lg resize-none text-xs" placeholder="Thank you for your business..." />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Contract / Disclaimer</label>
                                <textarea rows="2" className="w-full p-2.5 border border-gray-300 rounded-lg resize-none text-xs" placeholder="Valid for 30 days..." />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Internal notes</label>
                                <p className="text-xs text-gray-500 italic mb-1">Team only</p>
                                <textarea rows="2" className="w-full p-2.5 border border-gray-300 rounded-lg resize-none text-xs" placeholder="Add internal note..." />
                            </div>

                            {/* File Upload */}
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Note details</label>
                                <div className="border border-dashed border-gray-300 rounded-lg p-4 text-center">
                                    <Upload className="w-7 h-7 text-gray-400 mx-auto mb-1" />
                                    <p className="text-xs text-gray-600">Drag files or <span className="text-teal-600 font-medium">select</span></p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Footer Actions */}
            <div className="flex justify-between items-center py-4 px-4 border-t border-gray-200 bg-white rounded-b-xl">
                <button
                    onClick={step === 1 ? () => Navigate(-1) : prevStep}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-xs font-medium hover:bg-gray-50"
                >
                    {step === 1 ? 'Cancel' : 'Previous'}
                </button>

                <div className="flex gap-2">
                    {step === 3 && (
                        <div className="relative">
                            <button className="px-4 py-2 bg-teal-600 text-white rounded-lg text-xs font-medium flex items-center gap-1.5 shadow-sm hover:shadow">
                                Save And...
                                <ChevronDown className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    )}
                    <button
                        onClick={step < 3 ? nextStep : () => { }}
                        disabled={step === 1 && !selectedClient || step === 2 && selectedJobs.length === 0}
                        className="px-5 py-2 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-lg text-xs font-medium shadow-sm hover:shadow disabled:opacity-50"
                    >
                        {step === 3 ? 'Save Invoice' : 'Next Step'}
                    </button>
                </div>
            </div>
        </section>
    );
}